import { useCallback, useEffect, useRef, useState } from 'react';

export type AutoSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface UseAutoSaveOptions<T> {
  data: T;
  onSave: (data: T) => Promise<void>;
  debounceMs?: number;
  enabled?: boolean;
}

interface UseAutoSaveResult {
  status: AutoSaveStatus;
  lastSavedAt: Date | null;
  error: string | null;
  triggerSave: () => void;
}

export function useAutoSave<T>({
  data,
  onSave,
  debounceMs = 2000,
  enabled = true,
}: UseAutoSaveOptions<T>): UseAutoSaveResult {
  const [status, setStatus] = useState<AutoSaveStatus>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isSavingRef = useRef(false);
  const pendingDataRef = useRef<T | null>(null);
  const lastSavedDataRef = useRef<string | null>(null);
  const isInitialMountRef = useRef(true);

  const performSave = useCallback(
    async (dataToSave: T) => {
      const serialized = JSON.stringify(dataToSave);

      // Skip if data hasn't changed since last save
      if (serialized === lastSavedDataRef.current) {
        return;
      }

      // If already saving, queue this data for later
      if (isSavingRef.current) {
        pendingDataRef.current = dataToSave;
        return;
      }

      isSavingRef.current = true;
      setStatus('saving');
      setError(null);

      try {
        await onSave(dataToSave);
        lastSavedDataRef.current = serialized;
        setLastSavedAt(new Date());
        setStatus('saved');

        // Check if there's pending data to save
        if (pendingDataRef.current) {
          const pending = pendingDataRef.current;
          pendingDataRef.current = null;
          isSavingRef.current = false;
          // Save pending data after a short delay
          setTimeout(() => performSave(pending), 100);
          return;
        }
      } catch (err) {
        setStatus('error');
        setError(err instanceof Error ? err.message : 'Auto-save failed');
      } finally {
        isSavingRef.current = false;
      }
    },
    [onSave],
  );

  const triggerSave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    performSave(data);
  }, [data, performSave]);

  useEffect(() => {
    if (!enabled) return;

    // Skip auto-save on initial mount
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      // Store initial data as "saved" to avoid saving unchanged data
      lastSavedDataRef.current = JSON.stringify(data);
      return;
    }

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout for debounced save
    timeoutRef.current = setTimeout(() => {
      performSave(data);
    }, debounceMs);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [data, debounceMs, enabled, performSave]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return { status, lastSavedAt, error, triggerSave };
}
